import { IDENTITY_CONFIG, METADATA_OIDC } from '../utils/authConst';
import { UserManager, WebStorageStateStore, Log } from 'oidc-client-ts';
import { broadcastLogout } from '@packages/shared/session';

export default class AuthService {
  UserManager;

  constructor() {
    this.UserManager = new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      metadata: {
        ...METADATA_OIDC,
      },
      fetchRequestCredentials: 'include',
    });
    // Logger
    // Log.logger = console;
    // Log.level = Log.DEBUG;
    Log.setLogger(console);
    Log.setLevel(Log.DEBUG);
    this.UserManager.events.addUserLoaded((user) => {
      if (window.location.href.includes('callback')) {
        this.navigateToScreen();
      }
    });

    // Retry wrapper for token refresh
    const retryRefresh = async (maxRetries = 5, delay = 2000): Promise<any> => {
      let attempt = 0;
      while (attempt < maxRetries) {
        try {
          const user = await this.UserManager.signinSilent();
          if (user?.access_token) {
            return user;
          }
          throw new Error('No access token received');
        } catch (err) {
          console.warn(`Retry ${attempt + 1} failed`, err);
          if (attempt === maxRetries - 1) {
            throw err;
          }
          await new Promise((res) => setTimeout(res, delay * (attempt + 1))); // backoff
          attempt++;
        }
      }
      throw new Error('All retries failed');
    };

    // Hook into expiring event (proactive refresh)
    this.UserManager.events.addAccessTokenExpiring(async () => {
      try {
        await retryRefresh();
        console.log('Token refresh successful');
      } catch (error) {
        console.error('Token refresh failed after retries', error);
        // Fallback to expired event handler
        void this.signInSilent();
      }
    });

    // Fallback handler for when token is already expired
    this.UserManager.events.addAccessTokenExpired(() => {
      console.warn('Access token expired, attempting silent refresh');
      void this.signInSilent();
    });
  }

  signInRedirectCallback = (): any => {
    localStorage.setItem('Notify', 'Yes');
    localStorage.setItem('loggedIn Time', `${String(new Date())}`);
    return this.UserManager.signinRedirectCallback();
  };

  getUser = async (): Promise<any> => {
    const user = await this.UserManager.getUser();
    if (user == null) {
      return await this.UserManager.signinRedirectCallback();
    }
    return user;
  };

  parseJwt = (token: string): any => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  };

  signInRedirect = (): any => {
    localStorage.setItem('redirectUri', window.location.pathname);
    void this.UserManager.signinRedirect({});
  };

  navigateToScreen = (): void => {
    window.location.replace('/');
  };

  isAuthenticated = (): boolean => {
    const oidcStorage = localStorage.getItem(
      `oidc.user:${IDENTITY_CONFIG.authority ?? ''}:${IDENTITY_CONFIG.client_id ?? ''}`
    );

    if (oidcStorage != null) {
      const parsedStorage = JSON.parse(oidcStorage);
      return !!(Boolean(parsedStorage) && Boolean(parsedStorage.access_token));
    }

    return false;
  };

  createSignInRequest = (): any => {
    return this.UserManager.createSigninRequest();
  };

  logout = async (): Promise<void> => {
    // Notify other tabs via BroadcastChannel (primary) and localStorage (backward compat)
    broadcastLogout();
    localStorage.setItem('logout_event', Date.now().toString());

    void this.UserManager.signoutRedirect({
      id_token_hint: localStorage.getItem('id_token'),
    });
    localStorage.removeItem('loggedIn Time');
    localStorage.removeItem('Notify');
    sessionStorage.clear();
    localStorage.clear();
    void this.UserManager.clearStaleState();
  };

  signInSilent = async (): Promise<string | undefined | void> => {
    return this.UserManager.signinSilent({
      scope: IDENTITY_CONFIG.scope,
      response_type: IDENTITY_CONFIG.response_type,
    })
      .then(async (user) => {
        await this.UserManager.signinSilentCallback();
        return user?.access_token;
        // void this.UserManager.signinRedirect({});
      })
      .catch((err) => {
        console.warn('[AuthService] signInSilent failed, NOT cascading to logout:', err);
      });
  };

  signinSilentCallback = (): any => {
    void this.UserManager.signinSilentCallback();
  };

  signOutRedirectCallback = async (): Promise<void> => {
    try {
      await this.UserManager.signoutRedirectCallback();
      localStorage.clear();
      window.location.replace(process.env.REACT_APP_PUBLIC_URL ?? '');
    } catch (err) {
      // Handle the error if necessary
    }

    await this.UserManager.clearStaleState();
  };

  getToken = (): string => {
    const oidcStorageString = localStorage.getItem(
      `oidc.user:${IDENTITY_CONFIG.authority ?? ''}:${IDENTITY_CONFIG.client_id ?? ''}`
    );
    const oidcStorage = oidcStorageString != null ? JSON.parse(oidcStorageString) : null;
    const token =
      oidcStorage != null && Boolean(oidcStorage.access_token) ? oidcStorage.access_token : null;
    return token;
  };

  getIdToken = (): string => {
    const oidcStorageString = localStorage.getItem(
      `oidc.user:${IDENTITY_CONFIG.authority ?? ''}:${IDENTITY_CONFIG.client_id ?? ''}`
    );
    const oidcStorage = oidcStorageString != null ? JSON.parse(oidcStorageString) : null;
    const idToken =
      oidcStorage != null && Boolean(oidcStorage.id_token) ? oidcStorage.id_token : null;
    return idToken;
  };

  getTokenForSignalR = async (): Promise<string> => {
    if (this.isTokenExpired()) {
      return (await this.signInSilent()) as string;
    }
    return this.getToken();
  };

  isTokenExpired = (): boolean => {
    const token = this.getIdToken();
    if (!token) return true;

    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return true;

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));

      if (!payload.exp) return true;

      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();

      return currentTime >= expirationTime;
    } catch (error) {
      return true;
    }
  };
}

let _instance: AuthService | null = null;

/**
 * Returns the singleton AuthService instance.
 * Prevents multiple UserManager instances from registering duplicate
 * event handlers (accessTokenExpiring, accessTokenExpired, userLoaded).
 */
export function getAuthService(): AuthService {
  if (!_instance) _instance = new AuthService();
  return _instance;
}
